// Blog → New category. M5, revised 2026-08-15.

import { Button, Card, Field, Input } from '@fajrarabi/ui';
import { blogSlug, slugifyBlog } from '@fajrarabi/zod-schemas/blog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ApiError } from '~/lib/api';
import { createCategory } from '~/lib/blog';

export const Route = createFileRoute('/_authed/blog-categories/new')({
  component: NewBlogCategoryPage,
});

function NewBlogCategoryPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [slugOverride, setSlugOverride] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const slug = slugOverride ?? slugifyBlog(name);
  const slugValid = slug !== '' && blogSlug.safeParse(slug).success;

  const createMut = useMutation({
    mutationFn: () => createCategory({ name: name.trim(), slug }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['blog', 'categories'] });
      void navigate({ to: '/blog-categories' });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Could not create the category.');
    },
  });

  return (
    <div className="mx-auto max-w-2xl">
      <header>
        <Button variant="ghost" onClick={() => void navigate({ to: '/blog-categories' })}>
          ← All categories
        </Button>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">New category</h1>
        <p className="mt-1 text-sm text-slate-600">
          One name, shown on both the Arabic and English blog — pick something that reads well in
          both places.
        </p>
      </header>

      {error && (
        <p role="alert" className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-700">
          {error}
        </p>
      )}

      <Card className="mt-6">
        <div className="p-4">
          <Field label="Name">
            {(fieldId) => (
              <Input
                id={fieldId}
                dir="auto"
                value={name}
                autoFocus
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>

          <Field
            label="Slug"
            className="mt-4"
            hint="Used in the ?category= filter link. Arabic names keep Arabic slugs."
            error={
              name.trim() && !slugValid
                ? 'Could not derive a valid slug from that name.'
                : undefined
            }
          >
            {(fieldId) => (
              <Input
                id={fieldId}
                dir="auto"
                value={slug}
                onChange={(e) => setSlugOverride(e.target.value)}
              />
            )}
          </Field>

          <div className="mt-6 flex gap-2">
            <Button
              onClick={() => createMut.mutate()}
              disabled={!name.trim() || !slugValid || createMut.isPending}
            >
              {createMut.isPending ? 'Creating…' : 'Create category'}
            </Button>
            <Button variant="ghost" onClick={() => void navigate({ to: '/blog-categories' })}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
